import { Capacitor } from '@capacitor/core';
import { getToken } from '../services/api';
import { isReloadingForStaleChunk } from './staleChunkReload';

/**
 * Crash reporting, to our own backend.
 *
 * Why this exists: production is a static GCS bucket. No server, no request
 * log, no nginx. A page that throws in a customer's browser leaves no trace
 * anywhere we can reach — the only signal was the customer phoning up. The
 * backend never had this gap (Cloud Run pipes everything to Cloud Logging),
 * so this covers the half that was invisible.
 *
 * Why not a hosted service: at this scale the volume does not justify one, and
 * keeping reports in our own Postgres means no third party holds other
 * tenants' data. The trade-off is real and worth stating plainly — there is no
 * source-map symbolication, so stack frames arrive minified. The bundle is
 * built with `sourcemap: true` and the .map files publish beside it, so a
 * frame can be resolved by hand when it matters.
 *
 * The API shape here (init / report) deliberately mirrors what a hosted SDK
 * would want, so swapping one in later is a change to this file and nothing
 * else.
 */

interface ErrorPayload {
  name: string;
  message: string;
  stack?: string;
  release: string;
  platform: string;
  path: string;
  source: 'render' | 'window' | 'unhandledrejection' | 'manual';
}

const ENDPOINT = '/client-errors';

/**
 * Per-session cap. A render loop can throw hundreds of times a second, and
 * without this the reporter becomes the outage — the browser spends its time
 * posting about a broken page instead of showing one. The server aggregates by
 * fingerprint anyway, so nothing is learned from the 21st report.
 */
const MAX_REPORTS_PER_SESSION = 20;

/** Same error twice in this window is the same event as far as anyone cares. */
const DEDUP_WINDOW_MS = 60_000;

let sent = 0;
const recentlySent = new Map<string, number>();

/** Cheap client-side identity. The authoritative fingerprint is computed server-side. */
function localKey(name: string, message: string): string {
  return `${name}::${message}`.slice(0, 300);
}

function shouldSend(name: string, message: string): boolean {
  if (sent >= MAX_REPORTS_PER_SESSION) return false;

  const key = localKey(name, message);
  const now = Date.now();
  const last = recentlySent.get(key);
  if (last && now - last < DEDUP_WINDOW_MS) return false;

  recentlySent.set(key, now);
  // Bounded: the cap above limits distinct keys, but a loop generating unique
  // messages would still grow this without the sweep.
  if (recentlySent.size > 100) {
    for (const [k, t] of recentlySent) {
      if (now - t > DEDUP_WINDOW_MS) recentlySent.delete(k);
    }
  }
  return true;
}

/**
 * Bounded, cycle-safe JSON for a value with nothing better to say for itself.
 *
 * Capped short and used only as a last resort — an arbitrary rejected object
 * can carry anything, and this endpoint writes to a database shared across
 * tenants. The same reasoning as `path` never carrying `search`.
 */
function serialize(value: object): string {
  try {
    const seen = new WeakSet<object>();
    const json = JSON.stringify(value, (_key, v) => {
      if (v && typeof v === 'object') {
        if (seen.has(v as object)) return '[Circular]';
        seen.add(v as object);
      }
      return v;
    });
    if (!json || json === '{}') return Object.prototype.toString.call(value);
    return json.slice(0, 500);
  } catch {
    return Object.prototype.toString.call(value);
  }
}

/**
 * Turn an arbitrary thrown value into a reportable name, message and stack.
 *
 * What this exists to stop: `new Error(String(value))`. On a plain object that
 * produces the message "[object Object]", and because the Error is constructed
 * HERE it also produces a stack pointing at this file. Both halves of the
 * report are then about the reporter rather than the crash — and they are
 * byte-identical for every non-Error thrown anywhere in the app, so the
 * server's fingerprint merges unrelated bugs into a single row that can never
 * be diagnosed. One such row was already in production.
 *
 * A value that was not an Error never had a stack, so none is returned for
 * one. A synthesized stack describing this file is worse than no stack: it
 * reads as a real crash site.
 */
function describe(value: unknown): { name: string; message: string; stack?: string } {
  if (value instanceof Error) {
    return {
      name: value.name || 'Error',
      message: value.message || String(value),
      stack: value.stack,
    };
  }

  if (typeof value === 'string') {
    return { name: 'NonError', message: value };
  }

  if (value && typeof value === 'object') {
    const ctor = (value as { constructor?: { name?: string } }).constructor?.name;
    const message = (value as { message?: unknown }).message;
    return {
      // A named class (DOMException, a plugin's own error type) is worth
      // keeping — it is part of the fingerprint. A bare object literal is not.
      name: ctor && ctor !== 'Object' ? ctor : 'NonError',
      message: typeof message === 'string' && message ? message : serialize(value),
    };
  }

  return { name: 'NonError', message: String(value) };
}

/**
 * Send a report.
 *
 * Every failure path is swallowed. A crash reporter that throws while
 * reporting a crash turns one bug into two, and the ErrorBoundary that called
 * it is already the last line of defence.
 */
export function reportError(
  error: unknown,
  source: ErrorPayload['source'] = 'manual'
): void {
  try {
    // The page is about to be replaced; anything thrown now is an echo of the
    // stale chunk, not a bug.
    if (isReloadingForStaleChunk()) return;

    const { name, message, stack } = describe(error);

    if (!shouldSend(name, message)) return;
    sent += 1;

    const payload: ErrorPayload = {
      name,
      message: message.slice(0, 2000),
      stack: stack?.slice(0, 10000),
      release: __APP_VERSION__,
      platform: Capacitor.getPlatform(),
      // Path only — never search or hash. Query strings carry tokens, and a
      // reset-password link in a crash report is a credential in a database.
      path: window.location.pathname.slice(0, 500),
      source,
    };

    const url = `${import.meta.env.VITE_API_BASE_URL || '/api'}${ENDPOINT}`;

    // keepalive so a report survives the navigation or tab close that often
    // follows a crash. Deliberately not the shared axios client: that carries
    // interceptors which can trigger a token refresh, and a refresh storm on
    // top of a crash is the opposite of helpful.
    void fetch(url, {
      method: 'POST',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        ...(authHeader() ?? {}),
      },
      body: JSON.stringify(payload),
    }).catch(() => {
      // Offline, blocked, or the backend is down — all unremarkable at the
      // moment a client is crashing. Nothing to do and nowhere to say it.
    });
  } catch {
    // Reporting must never be the thing that breaks.
  }
}

/**
 * Attach the access token when one is in memory, so the report can be
 * attributed to a user.
 *
 * Note the server takes the user id from this token and NEVER from the body.
 * A client-supplied user id on an unauthenticated endpoint is an invitation to
 * file crash reports against somebody else.
 */
function authHeader(): Record<string, string> | null {
  try {
    const token = getToken();
    return token ? { Authorization: `Bearer ${token}` } : null;
  } catch {
    return null;
  }
}

/**
 * Install global handlers.
 *
 * An ErrorBoundary only catches errors thrown during RENDER. Errors in event
 * handlers, promises and timers never reach one — they land here instead, and
 * between the two there is no gap.
 */
export function initErrorReporting(): void {
  window.addEventListener('error', (event) => {
    // Resource load failures (a missing image) also fire this with no error
    // object. They are not crashes and would drown the real ones.
    if (!event.error) return;
    reportError(event.error, 'window');
  });

  window.addEventListener('unhandledrejection', (event) => {
    // Already handled — staleChunkReload is reloading the page for it.
    if (event.defaultPrevented) return;
    reportError(event.reason, 'unhandledrejection');
  });
}

/**
 * Exposed for tests: the session caps are the part worth asserting, since they
 * are what stops the reporter amplifying an outage.
 */
export function __reporterState() {
  return { sent, tracked: recentlySent.size };
}

/** Test-only. Resets the per-session caps between cases. */
export function __resetReporter(): void {
  sent = 0;
  recentlySent.clear();
}
